import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext'; // ✅ Fetch real user
const API_BASE_URL = 'https://sardar-jewellers.com/api/cart';
// const API_BASE_URL = 'http://localhost:8080/api/cart';

// ✅ Matches exact properties coming from your UserCart.java backend
export interface CartItem {
  cartId: number; 
  userSrno: number;
  productId: string;
  productTitle: string;
  productImage: string;
  price: number; 
  quantity: number;
}

interface ShopContextType {
  cartItems: CartItem[];
  cartLoading: boolean; 
  addItemToCart: (item: Omit<CartItem, 'cartId' | 'userSrno'>) => Promise<void>; 
  removeItemFromCart: (cartId: number) => Promise<void>;
  updateItemQuantity: (cartId: number, newQuantity: number) => Promise<void>;
  wishlistItems: any[]; 
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export const useShop = () => {
  const context = useContext(ShopContext);
  if (context === undefined) throw new Error('useShop must be used within a ShopProvider');
  return context;
};

export const ShopProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [wishlistItems, setWishlistItems] = useState<any[]>([]); 
  const [cartLoading, setCartLoading] = useState(true);

  const { user } = useAuth(); // ✅ Get actual logged in user

  const fetchCartItems = useCallback(async () => {
    if (!user) {
        setCartItems([]);
        setCartLoading(false);
        return;
    }
    setCartLoading(true);
    try {
      const response = await axios.get<any[]>(`${API_BASE_URL}/${user.srNo}`);
      
      const fetchedItems: CartItem[] = response.data.map(item => ({
        cartId: item.cartId, 
        userSrno: item.userSrno,
        productId: String(item.productId),
        productTitle: item.productTitle,
        productImage: item.productImage,
        price: parseFloat(item.price), 
        quantity: item.quantity,
      }));

      setCartItems(fetchedItems); 
    } catch (error) {
      console.error("Error fetching cart items:", error);
    } finally {
      setCartLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  const addItemToCart = async (item: Omit<CartItem, 'cartId' | 'userSrno'>) => {
    if(!user) return alert("Please log in to add items.");
    try {
      const requestBody = { ...item, userSrno: user.srNo };
      await axios.post(`${API_BASE_URL}/add`, requestBody);
      await fetchCartItems(); 
    } catch (error) { console.error("Error adding to cart:", error); }
  };

  const removeItemFromCart = async (cartId: number) => {
    if (window.confirm("Remove this item?")) {
        try {
            await axios.delete(`${API_BASE_URL}/remove/${cartId}`);
            await fetchCartItems();
        } catch (error) { console.error("Error removing:", error); }
    }
  };

  const updateItemQuantity = useCallback(async (cartId: number, newQuantity: number) => {
        if (newQuantity < 1) {
            if (window.confirm("Remove item?")) await removeItemFromCart(cartId);
            return;
        }
        try {
            await axios.put(`${API_BASE_URL}/update/${cartId}?quantity=${newQuantity}`);
            setCartItems(prevItems => prevItems.map(item => 
                item.cartId === cartId ? { ...item, quantity: newQuantity } : item
            ));
        } catch (error) {
            console.error("Error updating quantity:", error);
            fetchCartItems(); 
        }
    }, [fetchCartItems]); 

  return (
    <ShopContext.Provider value={{ cartItems, cartLoading, addItemToCart, removeItemFromCart, wishlistItems, updateItemQuantity }}>
      {children}
    </ShopContext.Provider>
  );
};